import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { useMusicT } from './kit';
import {
  MUSIC_GROUP_TITLES,
  MUSIC_HOME_PATH,
  MUSIC_PAGE_LINKS,
  type MusicPageLink,
  musicPagePath,
} from './links';

const GROUPS = Object.keys(MUSIC_GROUP_TITLES) as MusicPageLink['group'][];

/** Every music page, so visitors (and crawlers) can reach each one. */
export default function Explore({ currentId }: { currentId: string }) {
  const { t } = useMusicT();

  return (
    <section className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-wrap items-end justify-between gap-6'>
          <SectionHeading
            size='md'
            title={t('More ways to make music.')}
            tail={t('Pick a style or a purpose.')}
          />
          {currentId !== 'main' && (
            <Link
              href={MUSIC_HOME_PATH}
              className='inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
            >
              {t('AI Music Generator')}
              <ArrowGlyph />
            </Link>
          )}
        </div>

        <div className='mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4'>
          {GROUPS.map((group) => (
            <div key={group}>
              <h3
                className={clsxm(
                  'text-xs font-semibold uppercase tracking-[0.14em]',
                  tone.quiet,
                )}
              >
                {t(MUSIC_GROUP_TITLES[group])}
              </h3>
              <ul className='mt-4 flex flex-wrap gap-2'>
                {MUSIC_PAGE_LINKS.filter((link) => link.group === group).map(
                  (link) => {
                    const current = link.id === currentId;
                    return (
                      <li key={link.id}>
                        <Link
                          href={musicPagePath(link.id)}
                          aria-current={current ? 'page' : undefined}
                          className={clsxm(
                            'inline-flex rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 transition',
                            current
                              ? 'bg-[#10101c] text-white ring-[#10101c] dark:bg-white dark:text-[#10101c] dark:ring-white'
                              : clsxm(
                                  'bg-white ring-[#0f0f28]/[0.08] hover:text-primary-700 hover:ring-primary-300 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:text-primary-300',
                                  tone.body,
                                ),
                          )}
                        >
                          {t(link.name)}
                        </Link>
                      </li>
                    );
                  },
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
